<template>
  <header
    :class="{ 'is-mobile': isMobile }"
    class="the-user"
  >
    <div class="the-user__left">
      <button
        v-if="isMobile"
        class="icon menuOpenBtn"
        @click="$emit('toggle')"
      >
        <i class="fas fa-bars" />
      </button>
    </div>
    <div class="the-user__right">
      <template
        v-if="
          user.name
            && !hasRewardsDisabled
            && !isInternalUser
        "
      >
        <LiveloPoints class="livelo-points" />
      </template>

      <TheNotifications v-if="user.name" />

      <span
        :class="{ 'no-user': !user.name }"
        class="greetings"
      >
        {{ welcomeMessage }}
      </span>

      <button
        :class="{ 'no-user': !user.name }"
        class="icon"
        @click="open = !open"
      >
        <i
          :class="icon"
          class="fas"
        />
      </button>

      <aside
        v-show="open"
        ref="menu"
        class="menu px-4 py-5"
      >
        <p class="menu-label">
          Configurações
        </p>

        <ul class="menu-list">
          <template v-if="!isInternalUser">
            <li v-if="!hasRewardsDisabled">
              <span @click="livelo">Meus benefícios</span>
            </li>
          </template>
          <li><span @click="edit">Editar usuário</span></li>
          <li class="is-flex is-align-items-center is-fullwidth">
            <span @click="terms">
              <span>Termos e Políticas</span>
            </span>
          </li>
          <li class="is-flex is-align-items-center is-fullwidth">
            <span @click="guidelines">
              <span>Roteiros operacionais</span>
            </span>
          </li>
          <li><span @click="reloadSystem">Atualizar sistema</span></li>
          <li><span @click="logout">Sair</span></li>
        </ul>

        <p class="menu-label">
          <small class="version">Version {{ version }}</small>
        </p>
      </aside>
    </div>
  </header>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import { toast } from 'bulma-toast';

import config from '@/data/config';

import { accounts as accountsAPI } from '@/services';

import { useAppVersion } from '@/composables/useAppVersion';
import loadingMixin from '@/mixins/loadingView.mixin';
import LiveloPoints from '@/components/LiveloPoints.vue';
import TheNotifications from '@/components/TheNotifications.vue';

export default {
  name: 'TheUser',
  components: {
    LiveloPoints,
    TheNotifications,
  },
  mixins: [
    loadingMixin,
  ],
  data: () => ({
    open: false,
  }),
  computed: {
    ...mapState({
      user: (state) => state.user.userInfo,
    }),
    ...mapGetters({
      isMobile: 'app/isMobile',
      isInternalUser: 'user/isInternalUser',
    }),
    icon() {
      if (!this.user.name) return 'fa-exclamation';

      return this.open
        ? 'fa-times'
        : 'fa-user-cog';
    },
    version() {
      const deployVersion = config?.DEPLOY_VERSION;
      if (!deployVersion) return '—';

      try {
        const date = new Date(Number(deployVersion) * 1000);
        if (Number.isNaN(date.getTime())) return String(deployVersion);

        return `${
          Math.abs(date.getFullYear() - 2021) // major
        }.${
          date.getMonth() + 1
        }.${
          date.getDate()
        }.${
          String(date.getHours()).padStart(2, '0')
        }${
          String(date.getMinutes()).padStart(2, '0')
        }`;
      } catch {
        return String(deployVersion);
      }
    },
    hasRewardsDisabled() {
      return this.user?.rewards_disabled || false;
    },
    welcomeMessage() {
      if (this.user.name) return this.user.name;
      return 'no user';
    },
  },
  watch: {
    open: {
      handler(value) {
        if (value) {
          window.addEventListener('click', this.clickedOutside, true);
          window.addEventListener('keydown', this.escape);
        } else {
          window.removeEventListener('click', this.clickedOutside, true);
          window.removeEventListener('keydown', this.escape);
        }
      },
      immediate: true,
    },
  },
  beforeDestroy() {
    window.removeEventListener('click', this.clickedOutside, true);
    window.removeEventListener('keydown', this.escape);
  },
  methods: {
    ...mapActions({
      setToken: 'app/SET_TOKEN',
    }),

    // handlers
    handlerError(err) {
      let message = err?.response?.data?.error?.message
        ?? err?.response?.data?.message
        ?? err?.error?.response?.data?.error?.message
        ?? err?.error?.data?.message
        ?? err?.message
        ?? err;

      if (err?.response?.data?.error?.errors) {
        const errors = Object.values(err?.response?.data?.error?.errors);

        message = message.concat(`<br> - ${errors.map((error) => error).join('<br> - ')}`);
      }

      toast({
        message,
        type: 'is-danger',
        dismissible: true,
        pauseOnHover: true,
        animate: { in: 'fadeIn', out: 'fadeOut' },
        position: 'top-center',
        closeOnClick: true,
        duration: 10000,
      });
    },

    // handlers :: menu
    clickedOutside(ev) {
      const clickedEl = ev.target.closest('.menu');
      if (clickedEl === null) this.close();
    },
    escape(event) {
      if (event.key === 'Escape') this.close();
    },
    close() {
      this.open = false;
    },

    // items actions
    referral() {
      this.close();
      this.$router.push({ name: 'referral-links' });
    },
    livelo() {
      this.close();
      this.$router.push({ name: 'livelo-statement' });
    },
    edit() {
      this.close();
      this.$router.push({ name: 'edit-user', params: { id: this.user.id } });
    },
    terms() {
      this.close();
      this.$router.push({ name: 'terms' });
    },
    guidelines() {
      this.close();
      this.$router.push({ name: 'guidelines' });
    },
    reloadSystem() {
      this.close();
      const { clearVersionGuard } = useAppVersion();
      clearVersionGuard();
      window.location.reload();
    },
    async logout() {
      this.close();

      this.loading = true;

      await accountsAPI
        .logoutUser()
        .then((response) => {
          if (response.data.data.message) {
            this.setToken(false);
            this.$router.push({ name: 'login' });
          }
        })
        .catch((err) => {
          this.handlerError(err);
          this.$router.push({ name: 'login' });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/src/_textVariables.scss';

.the-user {
  --size: 40px;

  position: relative;

  display: flex;
  padding: 1rem;
  background-color: $color-primary;

  justify-content: space-between;
  align-items: center;

  &.is-mobile {
    --size: 30px;

    .menuOpenBtn {
      background-color: transparent;
      margin-left: 0;

      i {
        color: #333;
        font-size: 1rem;
      }
    }

    i {
      font-size: 0.725rem;
    }
  }

  > div {
    display: flex;
    align-items: center;
  }

  &__right {
    gap: .5rem;

    @media (min-width: 768px) {
      gap: 1rem;
    }
  }
}

.greetings {
  text-align: right;

  font-size: 1rem;
  color: #333;
  line-height: 1;
}

.livelo-points {
  @media (min-width: 768px) {
    margin-right: 1rem;
  }
}

.icon {
  display: flex;
  width: var(--size);
  height: var(--size);
  border: none;
  border-radius: 50%;
  background-color: #333;

  cursor: pointer;
  align-items: center;
  justify-content: center;
  text-align: center;

  i {
    color: $color-primary;
    font-size: 1.125rem;

    &.fa-user-cog {
      margin-left: 2.5px;
    }
  }
}

.no-user {
  &.greetings {
    font: {
      style: italic;
      weight: 400;
    }
  }
}

.version {
  padding: 0.5rem 0.75rem;
  color: var(--color-secondary);
}

.menu {
  position: absolute;
  top: calc(100% - 7.5px);
  right: 15px;
  z-index: 999;

  border-radius: 3px;
  min-width: 220px;
  background-color: #fff;
  box-shadow: 0 1px 1px 1px rgba(10, 10, 10, 0.1);

  li > span {
    display: inline-flex;
    width: 100%;
    border-radius: 2px;
    padding: .5em .75em;

    color: #4a4a4a;

    cursor: pointer;
    align-items: center;

    &:hover {
      background-color: hsl(0, 0%, 96%);
      color: hsl(0, 0%, 21%);
    }
  }
}

.tag {
  display: inline-flex;

  align-items: center;

  &.is-user {
    background-color: rgb(223, 216, 253);
    color: rgb(94, 77, 178)
  }
}

.is-fullwidth {
  width: 100%;
}
</style>
